import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadGoogleGsiScript, renderGoogleButton } from '../../utils/googleAuth';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const getRedirectTo = (searchParams: URLSearchParams): string => {
  const redirect = searchParams.get('redirect');
  if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
    return redirect;
  }
  return '/dashboard';
};

const Login: React.FC = () => {
  const { login, loginWithGoogle, verifyToken, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = getRedirectTo(searchParams);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const googleButtonRenderedRef = useRef(false);
  const handleGoogleCredentialRef = useRef<(credential: string) => void>(() => {});
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googleButtonReady, setGoogleButtonReady] = useState(false);

  // По ссылке из письма — предзаполняем форму (email + код), без авто-отправки
  useEffect(() => {
    const token = searchParams.get('token');
    const emailParam = searchParams.get('email');
    if (token && emailParam) {
      setEmail(decodeURIComponent(emailParam));
      setCode(token);
      setIsCodeSent(true);
    }
  }, [searchParams]);

  // Редирект если уже авторизован (в т.ч. на /billing после «Go Premium»)
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo);
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const handleGoogleCredential = useCallback(
    async (credential: string) => {
      setIsGoogleLoading(true);
      setError(null);
      try {
        await loginWithGoogle(credential);
        navigate(redirectTo);
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Google login failed. Please try again.');
        }
      } finally {
        setIsGoogleLoading(false);
      }
    },
    [loginWithGoogle, navigate, redirectTo],
  );

  handleGoogleCredentialRef.current = handleGoogleCredential;

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleButtonReady || isCodeSent || googleButtonRenderedRef.current) {
      return;
    }

    let cancelled = false;

    loadGoogleGsiScript()
      .then(() => {
        if (cancelled || googleButtonRenderedRef.current) return;

        const container = googleButtonRef.current;
        if (!container) return;

        renderGoogleButton(container, {
          clientId: GOOGLE_CLIENT_ID,
          width: container.offsetWidth,
          onCredential: (credential) => {
            void handleGoogleCredentialRef.current(credential);
          },
        });
        googleButtonRenderedRef.current = true;
      })
      .catch(() => {
        if (!cancelled) {
          setError('Could not load Google login. Please use email login.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [googleButtonReady, isCodeSent]);

  useEffect(() => {
    if (isCodeSent) return;

    const container = googleButtonRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      if (container.offsetWidth > 0) {
        setGoogleButtonReady(true);
      }
    });
    observer.observe(container);

    if (container.offsetWidth > 0) {
      setGoogleButtonReady(true);
    }

    return () => observer.disconnect();
  }, [isCodeSent]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await login(email);
      setIsCodeSent(true);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Error sending verification code');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length < 6) {
      setError('Code must be at least 6 characters');
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      await verifyToken(code, email);
      setTimeout(() => {
        navigate(redirectTo);
      }, 1000);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Invalid or expired code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isCodeSent) {
    return (
      <div className="content login-page-wrap">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card-glass login-form login-form-shell"
          >
          <div className="w-16 h-16 bg-[#4ff6e6]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-[#4ff6e6]" />
          </div>
          <h2 className="login-title">
            Verify Email
          </h2>
          <p className="login-subtitle">
            Enter the verification code sent to <span className="login-subtitle-email">{email}</span>
          </p>

          <form onSubmit={handleCodeSubmit} className="login-form-fields">
            <div>
              <input
                type="email"
                value={email}
                required
                placeholder="your@email.com"
                className="input-glass login-input login-input-readonly"
                readOnly
              />
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\s/g, ''));
                  setError(null);
                }}
                placeholder="Verification code"
                required
                maxLength={6}
                autoFocus
                className="input-glass login-input login-code-input"
              />
            </div>

            {error && (
              <div className="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !code || code.length < 6}
              className="btn btn-gold login-submit"
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
          </motion.div>
      </div>
    );
  }

  return (
    <div className="content login-page-wrap">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-glass login-form login-form-shell"
        >
        <h2 className="login-title">
          Log in or join
        </h2>
        <p className="login-subtitle">
          We'll send a code to your email — no password. New users get an account automatically.
        </p>

        {GOOGLE_CLIENT_ID && (
          <>
            <div className={`login-google-section ${isGoogleLoading ? 'login-google-section--loading' : ''}`}>
              <div ref={googleButtonRef} className="login-google-button-host" />
            </div>
            <div className="login-divider">
              <div className="login-divider-line" />
              <span className="login-divider-text">or</span>
              <div className="login-divider-line" />
            </div>
          </>
        )}

        <form onSubmit={handleEmailSubmit} className="login-form-fields">
          <div>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="input-glass login-input"
            />
          </div>

          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !email}
            className="btn btn-gold login-submit"
          >
            {isLoading ? 'Sending code...' : 'Send Verification Code'}
          </button>
        </form>
        </motion.div>
    </div>
  );
};

export default Login;
